(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-fb-objective-experience]';
  const TAB_SELECTOR = '[data-fb-subobjective-tab]';
  const PANEL_SELECTOR = '[data-fb-subobjective-panel]';
  const MOTION_SELECTOR = '.fb-objective-tabs__tab, [data-fb-related-card]';
  const isEnglish = document.documentElement.lang?.toLowerCase().startsWith('en');
  const messages = isEnglish
    ? {
        invalidPair: 'The pair could not be validated. Review both products separately.',
        adding: 'Adding both products…',
        addError: 'Both products could not be added.',
        cartError: 'The cart could not be updated.',
        success: 'Both products have been added. You can review quantities and variants in the cart.',
        fallbackError: 'The action could not be completed.',
        addedButton: 'Added · 2 products',
      }
    : {
        invalidPair: 'No se ha podido validar la pareja. Revisa los dos productos por separado.',
        adding: 'Añadiendo los dos productos…',
        addError: 'No se han podido añadir los dos productos.',
        cartError: 'El carrito no ha podido actualizarse.',
        success: 'Los dos productos se han añadido. Puedes revisar cantidades y variantes en el carrito.',
        fallbackError: 'No se ha podido completar la acción.',
        addedButton: 'Añadidos · 2 productos',
      };

  const presentationStyle = document.createElement('style');
  presentationStyle.dataset.fbObjectivePresentation = 'true';
  presentationStyle.textContent = `
    .fb-objective-guide { display: none !important; }
    .fb-objective-experience__hero-media {
      width: 100% !important;
      max-width: none !important;
      margin-inline: auto !important;
      justify-self: stretch !important;
    }
    .fb-objective-experience__hero-media img {
      display: block !important;
      width: 100% !important;
      max-width: none !important;
      margin-inline: auto !important;
      object-fit: cover !important;
      object-position: 50% 50% !important;
    }
  `;
  document.head.append(presentationStyle);

  const normalizeObjectivePresentation = (root) => {
    root.querySelector('.fb-objective-guide')?.remove();

    const media = root.querySelector('.fb-objective-experience__hero-media');
    const image = media?.querySelector('img');
    if (media) {
      media.style.width = '100%';
      media.style.maxWidth = 'none';
      media.style.marginInline = 'auto';
      media.style.justifySelf = 'stretch';
    }
    if (image) {
      image.style.display = 'block';
      image.style.width = '100%';
      image.style.maxWidth = 'none';
      image.style.marginInline = 'auto';
      image.style.objectFit = 'cover';
      image.style.objectPosition = '50% 50%';
    }
  };

  const track = (name, parameters) => {
    window.FitobotanicAnalytics?.track?.(name, parameters);
  };

  const activate = (root, key, options = {}) => {
    const tabs = [...root.querySelectorAll(TAB_SELECTOR)];
    const panels = [...root.querySelectorAll(PANEL_SELECTOR)];
    const targetTab = tabs.find((tab) => tab.dataset.fbSubobjectiveTab === key);
    const targetPanel = panels.find((panel) => panel.dataset.fbSubobjectivePanel === key);
    if (!targetTab || !targetPanel) return;

    const alreadyActive = targetTab.getAttribute('aria-selected') === 'true' && !targetPanel.hidden;
    if (alreadyActive && !options.force) {
      if (options.focus) targetTab.focus({ preventScroll: true });
      return;
    }

    tabs.forEach((tab) => {
      const selected = tab === targetTab;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    });
    panels.forEach((panel) => {
      panel.hidden = panel !== targetPanel;
    });

    if (options.focus) targetTab.focus({ preventScroll: true });
    if (options.updateUrl && window.history?.replaceState) {
      window.history.replaceState(null, '', `#pack-${encodeURIComponent(key)}`);
    }
    if (options.scroll) targetPanel.scrollIntoView({ behavior: 'smooth', block: 'start' });

    if (!options.silent) {
      track('fitobotanic_subobjective_selected', {
        objective_key: root.dataset.fbObjectiveKey,
        subobjective_key: key,
        source: options.source || 'objective_page',
      });
    }
  };

  const setStatus = (panel, message, state = '') => {
    const status = panel.querySelector('[data-fb-pack-status]');
    if (!status) return;
    status.textContent = message;
    status.dataset.state = state;
  };

  const addPair = async (root, panel, button) => {
    const coreVariant = Number.parseInt(button.dataset.coreVariant || '', 10);
    const supportVariant = Number.parseInt(button.dataset.supportVariant || '', 10);
    if (!coreVariant || !supportVariant || coreVariant === supportVariant) {
      setStatus(panel, messages.invalidPair, 'error');
      return;
    }

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    setStatus(panel, messages.adding, 'loading');

    const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((element) => element.dataset.sectionId)
      .filter(Boolean);
    const rootPath = window.Shopify?.routes?.root || '/';

    try {
      const response = await fetch(`${rootPath}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [
            { id: coreVariant, quantity: 1 },
            { id: supportVariant, quantity: 1 },
          ],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const result = await response.json();
      if (!response.ok || result.status || result.errors) {
        throw new Error(result.description || result.message || messages.addError);
      }

      const cartResponse = await fetch(`${rootPath}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!cartResponse.ok) throw new Error(messages.cartError);
      const cart = await cartResponse.json();

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: button.id,
            data: {
              source: 'fitobotanic-biaxial',
              itemCount: cart.item_count,
              variantId: `${coreVariant},${supportVariant}`,
              sections: result.sections || {},
              objectiveKey: root.dataset.fbObjectiveKey,
              subobjectiveKey: panel.dataset.fbSubobjectivePanel,
            },
          },
        })
      );

      setStatus(panel, messages.success, 'success');
      button.textContent = messages.addedButton;
      const cartLink = panel.querySelector('[data-fb-pack-cart-link]');
      if (cartLink) cartLink.hidden = false;
    } catch (error) {
      setStatus(panel, error instanceof Error ? error.message : messages.fallbackError, 'error');
      button.disabled = false;
    } finally {
      button.removeAttribute('aria-busy');
    }
  };

  const populateRevisit = (root) => {
    const target = document.querySelector(
      `[data-fb-objective-revisit="${CSS.escape(root.dataset.fbObjectiveKey || '')}"]`
    );
    if (!target) return;
    const list = target.querySelector('[data-fb-revisit-list]');
    if (!list) return;

    root.querySelectorAll(TAB_SELECTOR).forEach((tab) => {
      const link = document.createElement('a');
      link.href = `#pack-${encodeURIComponent(tab.dataset.fbSubobjectiveTab || '')}`;
      link.textContent = tab.querySelector('[data-fb-tab-title]')?.textContent?.trim() || tab.textContent.trim();
      link.dataset.fbRevisitKey = tab.dataset.fbSubobjectiveTab || '';
      list.append(link);
    });
    target.hidden = list.childElementCount === 0;
  };

  const prepareMotion = (root) => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
      return;
    }

    const elements = [...root.querySelectorAll(MOTION_SELECTOR)];
    if (!elements.length) return;
    root.classList.add('fb-motion-ready');

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );

    elements.forEach((element, index) => {
      element.style.transitionDelay = `${Math.min(index % 4, 3) * 55}ms`;
      observer.observe(element);
    });
  };

  const syncTabsAfterSwipe = (root, tabs) => {
    const tablist = root.querySelector('.fb-objective-tabs');
    if (!tablist || tabs.length < 2) return;

    let debounceTimer = null;

    const activateCenteredTab = () => {
      if (window.matchMedia('(min-width: 650px)').matches) return;

      const listRect = tablist.getBoundingClientRect();
      const listCenter = listRect.left + listRect.width / 2;
      const centeredTab = tabs.reduce((closest, tab) => {
        const rect = tab.getBoundingClientRect();
        const distance = Math.abs(rect.left + rect.width / 2 - listCenter);
        return distance < closest.distance ? { tab, distance } : closest;
      }, { tab: tabs[0], distance: Number.POSITIVE_INFINITY }).tab;

      activate(root, centeredTab.dataset.fbSubobjectiveTab, {
        source: 'subobjective_swipe',
      });
    };

    const scheduleActivation = () => {
      window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(activateCenteredTab, 140);
    };

    if ('onscrollend' in window) {
      tablist.addEventListener('scrollend', activateCenteredTab, { passive: true });
    }
    tablist.addEventListener('scroll', scheduleActivation, { passive: true });
    window.addEventListener('resize', scheduleActivation, { passive: true });
  };

  document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
    normalizeObjectivePresentation(root);

    const tabs = [...root.querySelectorAll(TAB_SELECTOR)];
    if (!tabs.length) return;

    const hashKey = decodeURIComponent(window.location.hash.replace(/^#pack-/, ''));
    const initialKey = tabs.some((tab) => tab.dataset.fbSubobjectiveTab === hashKey)
      ? hashKey
      : tabs[0].dataset.fbSubobjectiveTab;
    activate(root, initialKey, { source: 'initial_load', silent: true });
    track('fitobotanic_objective_experience_viewed', {
      objective_key: root.dataset.fbObjectiveKey,
      source: 'objective_page',
    });
    populateRevisit(root);
    prepareMotion(root);
    syncTabsAfterSwipe(root, tabs);

    root.querySelectorAll('.fb-objective-pack__accordions details').forEach((details) => {
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        track('fitobotanic_subobjective_detail_opened', {
          detail: details.querySelector('summary')?.textContent?.trim(),
        });
      });
    });

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => {
        activate(root, tab.dataset.fbSubobjectiveTab, {
          updateUrl: true,
          scroll: true,
          source: 'subobjective_selector',
        });
      });
      tab.addEventListener('keydown', (event) => {
        let nextIndex = index;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (index + 1) % tabs.length;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (index - 1 + tabs.length) % tabs.length;
        else if (event.key === 'Home') nextIndex = 0;
        else if (event.key === 'End') nextIndex = tabs.length - 1;
        else return;
        event.preventDefault();
        activate(root, tabs[nextIndex].dataset.fbSubobjectiveTab, {
          focus: true,
          updateUrl: true,
          source: 'keyboard',
        });
      });
    });

    root.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const labelButton = event.target.closest('[data-fb-label-open]');
      if (labelButton) {
        const dialog = root.querySelector('[data-fb-label-dialog]');
        const dialogImage = dialog?.querySelector('[data-fb-label-dialog-image]');
        const dialogTitle = dialog?.querySelector('[data-fb-label-dialog-title]');
        if (dialog && dialogImage && typeof dialog.showModal === 'function') {
          dialogImage.src = labelButton.dataset.labelSrc || '';
          dialogImage.alt = labelButton.dataset.labelAlt || '';
          if (dialogTitle) dialogTitle.textContent = labelButton.dataset.labelTitle || dialogTitle.textContent;
          dialog.showModal();
          track('fitobotanic_back_label_opened', { source: 'objective_page' });
        }
        return;
      }

      const closeButton = event.target.closest('[data-fb-label-close]');
      if (closeButton) {
        closeButton.closest('dialog')?.close();
        return;
      }

      const compareLink = event.target.closest('[data-fb-compare-priorities]');
      if (compareLink) {
        track('fitobotanic_compare_priorities_clicked', { source: 'objective_page' });
      }

      const button = event.target.closest('[data-fb-add-pair]');
      if (!button || button.disabled) return;
      const panel = button.closest(PANEL_SELECTOR);
      if (panel) addPair(root, panel, button);
    });

    root.querySelectorAll('[data-fb-label-dialog]').forEach((dialog) => {
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });
    });

    root.querySelectorAll('[data-fb-related-card] a').forEach((link) => {
      link.addEventListener('click', () => {
        track('fitobotanic_related_product_clicked', { source: 'objective_page' });
      });
    });
  });

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const link = event.target.closest('[data-fb-revisit-key]');
    if (!link) return;
    const root = document.querySelector(ROOT_SELECTOR);
    if (!root) return;
    event.preventDefault();
    activate(root, link.dataset.fbRevisitKey, {
      updateUrl: true,
      scroll: true,
      source: 'other_subobjectives',
    });
  });
})();
